import { MystudioApiError } from "./client";
import {
    V4AvailabilityDto,
    V4ContentListDto,
    V4HomeDto,
    V4GalleryPostDto,
    V4GallerySectionDto,
    V4InquiryDto,
    V4MeDto,
    V4NotificationDto,
    V4NotificationPreferencesDto,
    V4PageResultDto,
    V4PhotoAssetDto,
    V4PhotoDeliveryDto,
    V4ProductDto,
    V4ScheduleDto,
    V4ThemeDto,
    V4ReviewEventDto,
} from "./dto";
import {
    MystudioActionItem,
    MystudioCollection,
    MystudioContentItem,
    MystudioCursorPage,
    MystudioHome,
    MystudioGalleryPhoto,
    MystudioGalleryPost,
    MystudioGalleryPostPage,
    MystudioGallerySection,
    MystudioInquiry,
    MystudioMyPage,
    MystudioNotification,
    MystudioNotificationPreferences,
    MystudioPhotoAsset,
    MystudioPhotoAssetPage,
    MystudioPhotoDelivery,
    MystudioProduct,
    MystudioProductType,
    MystudioReviewEventState,
    MystudioReviewEventCatalog,
    MystudioReviewEventCatalogItem,
    MystudioSchedule,
    MystudioScheduleStatus,
    MystudioTheme,
    MystudioThemeCatalog,
    MystudioTermsDocument,
} from "./types";

function invalidResponse(resource: string, value: unknown): never {
    throw new MystudioApiError({
        message: `${resource} 응답 형식을 확인할 수 없습니다.`,
        kind: "invalid_response",
        details: value,
    });
}

function objectValue(value: unknown): Record<string, unknown> {
    return value && typeof value === "object" && !Array.isArray(value)
        ? value as Record<string, unknown>
        : {};
}

function text(...values: unknown[]): string {
    const found = values.find((value) => value !== undefined && value !== null && String(value).trim());
    return found === undefined ? "" : String(found).trim();
}

function numberValue(...values: unknown[]): number | undefined {
    const found = values.map(Number).find(Number.isFinite);
    return found;
}

function booleanValue(...values: unknown[]): boolean {
    const found = values.find((value) => value !== undefined && value !== null);
    if (typeof found === "string") {
        return ["true", "1", "y", "yes"].includes(found.trim().toLowerCase());
    }
    if (typeof found === "number") return found === 1;
    return found === true;
}

function normalizeAvailability<T>(
    raw: V4AvailabilityDto<T> | V4PageResultDto<T> | T[],
    resource: string,
): MystudioCollection<T> {
    if (Array.isArray(raw)) {
        return { items: raw, availability: "available", total: raw.length };
    }
    if (!raw || typeof raw !== "object") invalidResponse(resource, raw);
    const root = raw as V4PageResultDto<T>;
    const availability = root.availability === "unavailable"
        ? "unavailable"
        : root.availability === "empty" || (Array.isArray(root.items) && root.items.length === 0)
            ? "empty"
            : "available";
    const items = Array.isArray(root.items) ? root.items : [];
    if (!Array.isArray(root.items) && root.availability !== "unavailable") {
        invalidResponse(resource, raw);
    }
    return {
        items,
        availability,
        reasonCode: text(root.reasonCode, root.reason_code) || undefined,
        page: numberValue(root.page),
        pageSize: numberValue(root.limit, root.pageSize, root.page_size),
        total: numberValue(root.total) ?? items.length,
    };
}

function scheduleStatus(row: V4ScheduleDto): MystudioScheduleStatus {
    const rawValue = text(row.statusValue, row.status_value);
    const hasNumericValue = /^\d+$/.test(rawValue);
    const normalizedValue = hasNumericValue
        ? String(Number(rawValue))
        : rawValue.toLowerCase();
    const code = text(row.statusCode, row.status_code).toLowerCase();
    const label = text(row.statusLabel, row.status_label).toLowerCase();

    if (hasNumericValue) {
        if (["3", "4", "5"].includes(normalizedValue)) return "cancelled";
        if (normalizedValue === "1") return "completed";
        return "upcoming";
    }

    if (
        normalizedValue.includes("cancel")
        || normalizedValue.includes("\uCDE8\uC18C")
        || normalizedValue.includes("\uB178\uC1FC")
        || code.includes("cancel")
        || ["108", "110"].includes(code)
        || label.includes("\uCDE8\uC18C")
        || label.includes("\uB178\uC1FC")
    ) return "cancelled";
    if (
        normalizedValue.includes("complete")
        || normalizedValue === "done"
        || (
            normalizedValue.includes("\uC644\uB8CC")
            && !normalizedValue.includes("\uBBF8\uC644\uB8CC")
        )
        || code === "106"
        || code.includes("complete")
        || code === "done"
        || (label.includes("\uC644\uB8CC") && !label.includes("\uBBF8\uC644\uB8CC"))
    ) return "completed";
    return "upcoming";
}

function toScheduleDateTime(row: V4ScheduleDto): string {
    const date = text(row.date);
    const startTime = text(row.startTime, row.start_time);
    if (!date) return "";
    if (date.includes("T")) return date;
    return `${date}T${startTime || "00:00"}${startTime.split(":").length === 2 ? ":00" : ""}`;
}

function adaptPhotoAsset(value: unknown, index: number): MystudioPhotoAsset | null {
    const row = objectValue(value);
    const url = text(row.url, row.imageUrl, row.image_url, row.org_url);
    if (!url) return null;
    return {
        id: text(row.id, row.photoId, row.photo_id, index),
        url,
        thumbnailUrl: text(row.thumbnailUrl, row.thumbnail_url, row.small_url) || undefined,
        downloadUrl: text(row.downloadUrl, row.download_url) || undefined,
        fileName: text(row.fileName, row.file_name) || undefined,
        alt: text(row.alt) || undefined,
        title: text(row.title) || undefined,
        category: text(row.category, row.category_name) || undefined,
        officeName: text(row.officeName, row.office_name) || undefined,
        uploadedAt: text(row.uploadedAt, row.uploaded_at, row.created_at) || undefined,
    };
}

function adaptReviewEvent(value: unknown): MystudioReviewEventState | undefined {
    if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
    const row = value as Record<string, unknown>;
    const validStatuses = new Set([
        "not_applied", "pending", "accepted", "editing", "uploaded", "rejected", "expired",
    ]);
    const rawStatus = text(row.status, row.applicationStatus, row.application_status);
    const status = validStatuses.has(rawStatus) ? rawStatus as MystudioReviewEventState["status"] : "not_applied";
    return {
        id: text(row.id, row.reviewEventId, row.review_event_id) || undefined,
        availability: row.availability === "unavailable"
            ? "unavailable"
            : row.availability === "empty" ? "empty" : "available",
        reasonCode: text(row.reasonCode, row.reason_code) || undefined,
        eligible: Boolean(row.eligible ?? row.isEligible ?? row.is_eligible),
        status,
        applicationId: text(row.applicationId, row.application_id) || undefined,
        applicationEndsAt: text(row.applicationEndsAt, row.application_ends_at) || undefined,
        downloadExpiresAt: text(row.downloadExpiresAt, row.download_expires_at) || undefined,
        photos: Array.isArray(row.photos)
            ? row.photos.map(adaptPhotoAsset).filter(Boolean) as MystudioPhotoAsset[]
            : [],
    };
}

function adaptReviewEventCatalogItem(
    row: V4ReviewEventDto,
): MystudioReviewEventCatalogItem {
    const id = text(row.id);
    if (!id) invalidResponse("후기 이벤트", row);
    const terms = row.terms && typeof row.terms === "object"
        ? row.terms
        : null;
    const eligibility = row.eligibility && typeof row.eligibility === "object"
        ? row.eligibility
        : null;
    return {
        id,
        officeId: text(row.officeId, row.office_id) || undefined,
        title: text(row.title) || "후기 이벤트",
        description: text(row.description) || undefined,
        applicationStartAt: text(
            row.applicationStartAt,
            row.application_start_at,
        ) || undefined,
        applicationEndAt: text(
            row.applicationEndAt,
            row.application_end_at,
        ) || undefined,
        expectedAssetCount: numberValue(
            row.expectedAssetCount,
            row.expected_asset_count,
        ) ?? 0,
        consent: {
            required: booleanValue(row.consent?.required),
            text: text(row.consent?.text) || undefined,
        },
        terms: terms && text(terms.id) ? {
            id: text(terms.id),
            type: text(terms.type) || undefined,
            version: text(terms.version) || undefined,
            title: text(terms.title) || "후기 이벤트 이용약관",
            content: text(terms.content),
            effectiveFrom: text(
                terms.effectiveFrom,
                terms.effective_from,
            ) || undefined,
            active: booleanValue(terms.active),
        } : undefined,
        eligibility: {
            eligible: booleanValue(eligibility?.eligible),
            reasonCode: text(
                eligibility?.reasonCode,
                eligibility?.reason_code,
            ) || undefined,
        },
    };
}

export function adaptReviewEventCatalog(
    raw: V4AvailabilityDto<V4ReviewEventDto> | V4ReviewEventDto[],
): MystudioReviewEventCatalog {
    const collection = normalizeAvailability(raw, "후기 이벤트");
    return {
        ...collection,
        items: collection.items.map(adaptReviewEventCatalogItem),
    };
}

export function adaptNotificationPreferences(
    raw: V4NotificationPreferencesDto,
): MystudioNotificationPreferences {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
        invalidResponse("알림 설정", raw);
    }
    const availability = raw.availability === "unavailable"
        ? "unavailable"
        : raw.availability === "empty"
            ? "empty"
            : "available";
    return {
        availability,
        reasonCode: text(raw.reasonCode, raw.reason_code) || undefined,
        marketingEnabled: booleanValue(
            raw.marketingEnabled,
            raw.marketing_enabled,
        ),
        scheduleEnabled: booleanValue(
            raw.scheduleEnabled,
            raw.schedule_enabled,
        ),
        reviewEventEnabled: booleanValue(
            raw.reviewEventEnabled,
            raw.review_event_enabled,
        ),
        productEnabled: booleanValue(
            raw.productEnabled,
            raw.product_enabled,
        ),
        serviceEnabled: booleanValue(
            raw.serviceEnabled,
            raw.service_enabled,
        ),
        quietHoursStart: text(
            raw.quietHoursStart,
            raw.quiet_hours_start,
        ) || null,
        quietHoursEnd: text(
            raw.quietHoursEnd,
            raw.quiet_hours_end,
        ) || null,
    };
}

export function adaptSchedule(row: V4ScheduleDto): MystudioSchedule {
    const id = text(row.id, row.scheduleId, row.schedule_id);
    if (!id) invalidResponse("촬영일정", row);
    const status = scheduleStatus(row);
    const theme = row.theme && typeof row.theme === "object" ? row.theme : null;
    const selectedThemeIds = theme?.id ? [String(theme.id)] : [];
    const explicitSelection = row.themeSelection;
    const guide = row.guide && typeof row.guide === "object" ? row.guide : null;
    const preparationItems = guide && Array.isArray(guide.preparationItems)
        ? guide.preparationItems
        : guide && Array.isArray(guide.preparation_items)
            ? guide.preparation_items
            : guide && Array.isArray(guide.checklist)
                ? guide.checklist
                : [];
    const originalPhotos = objectValue(row.originalPhotos);
    const originalCount = numberValue(originalPhotos.count, originalPhotos.total);
    const originalStatus = text(originalPhotos.status);

    return {
        id,
        title: text(row.title) || "촬영 일정",
        scheduledAt: toScheduleDateTime(row),
        status,
        office: text(row.officeId, row.office_id) ? {
            id: text(row.officeId, row.office_id),
            name: text(row.officeName, row.office_name) || "스튜디오 정보 없음",
        } : undefined,
        shootType: text(row.type) || undefined,
        productTitle: text(row.productTitle, row.product_title) || undefined,
        thumbnailUrl: text(row.imageUrl, row.image_url, theme?.imageUrl, theme?.image_url) || undefined,
        studioNotice: text(guide?.content) || undefined,
        preparationItems,
        themeSelection: {
            required: Boolean(
                explicitSelection?.required
                ?? (status === "upcoming" && !theme),
            ),
            locked: Boolean(explicitSelection?.locked ?? theme?.locked),
            requiredCount: numberValue(explicitSelection?.requiredCount) ?? 1,
            selectedThemeIds: Array.isArray(explicitSelection?.selectedThemeIds)
                ? explicitSelection?.selectedThemeIds?.map(String) || []
                : selectedThemeIds,
        },
        originalPhotos: Object.keys(originalPhotos).length > 0 ? {
            status: ["processing", "available", "expired"].includes(originalStatus)
                ? originalStatus as "processing" | "available" | "expired"
                : "not_ready",
            count: originalCount || 0,
            expiresAt: text(originalPhotos.expiresAt, originalPhotos.expires_at) || undefined,
            downloadUrl: text(originalPhotos.downloadUrl, originalPhotos.download_url) || undefined,
        } : undefined,
        reviewEvent: adaptReviewEvent(row.reviewEvent),
    };
}

export function adaptSchedules(
    raw: V4PageResultDto<V4ScheduleDto> | V4ScheduleDto[],
): MystudioCollection<MystudioSchedule> {
    const collection = normalizeAvailability(raw, "촬영일정");
    return { ...collection, items: collection.items.map(adaptSchedule) };
}

function productType(value: unknown): MystudioProductType {
    const type = text(value).toLowerCase();
    if (type === "2" || type.includes("album") || type.includes("앨범")) return "album";
    if (type === "3" || type.includes("frame") || type.includes("액자")) return "frame";
    if (type.includes("package") || type.includes("패키지")) return "package";
    return "other";
}

function productStatus(row: V4ProductDto): MystudioProduct["status"] {
    const statusRoot = typeof row.status === "object" && row.status !== null
        ? row.status as { code?: string | number; label?: string }
        : null;
    const value = text(
        statusRoot?.code,
        statusRoot?.label,
        row.status,
        row.statusCode,
        row.status_code,
        row.statusLabel,
        row.status_label,
    ).toLowerCase();
    if (value.includes("complete") || value.includes("완료") || value === "5") return "completed";
    if (value.includes("production") || value.includes("제작") || value === "6") return "in_production";
    if (value.includes("submit") || value.includes("접수") || value === "3") return "submitted";
    if (value.includes("draft") || value.includes("작성")) return "draft";
    if (value.includes("expire") || value.includes("종료")) return "expired";
    return "not_started";
}

export function adaptProduct(row: V4ProductDto): MystudioProduct {
    const id = text(row.id, row.contractProductId, row.contract_product_id);
    if (!id) invalidResponse("계약상품", row);
    return {
        id,
        title: text(row.name, row.title) || "계약 상품",
        type: productType(row.type),
        status: productStatus(row),
        thumbnailUrl: text(row.imageUrl, row.image_url) || undefined,
        optionLabel: text(row.optionLabel, row.option_label) || (
            numberValue(row.quantity) ? `수량 ${numberValue(row.quantity)}개` : undefined
        ),
        dueAt: text(row.dueAt, row.due_at) || undefined,
        requiredPhotoCount: numberValue(
            row.requestedPhotoCount,
            row.requested_photo_count,
            row.minPhotoCount,
            row.min_photo_count,
        ) || undefined,
        maxPhotoCount: numberValue(row.maxPhotoCount, row.max_photo_count) || undefined,
        selectedPhotoCount: numberValue(
            row.selectedPhotoCount,
            row.selected_photo_count,
        ) || undefined,
        applicationId: text(row.applicationId, row.application_id) || undefined,
        requestable: row.requestable !== false,
    };
}

export function adaptProducts(
    raw: V4AvailabilityDto<V4ProductDto> | V4PageResultDto<V4ProductDto> | V4ProductDto[],
): MystudioCollection<MystudioProduct> {
    const collection = normalizeAvailability(raw, "계약상품");
    return { ...collection, items: collection.items.map(adaptProduct) };
}

export function adaptPhotoDeliveries(
    raw: V4AvailabilityDto<V4PhotoDeliveryDto> | V4PageResultDto<V4PhotoDeliveryDto> | V4PhotoDeliveryDto[],
): MystudioCollection<MystudioPhotoDelivery> {
    const collection = normalizeAvailability(raw, "촬영 사진");
    return {
        ...collection,
        items: collection.items.map((row) => {
            const id = text(row.id, row.deliveryId, row.delivery_id);
            const scheduleId = text(row.scheduleId, row.schedule_id);
            if (!id || !scheduleId) invalidResponse("촬영 사진", row);
            return {
                id,
                scheduleId,
                scheduleDate: text(row.scheduleDate, row.schedule_date) || undefined,
                type: text(row.type, row.photoType, row.photo_type) || undefined,
                status: text(row.status) || undefined,
                photoCount: numberValue(row.photoCount, row.photo_count) || 0,
                expiresAt: text(row.expiresAt, row.expires_at) || undefined,
            };
        }),
    };
}

export function adaptPhotoAssets(
    raw: V4AvailabilityDto<V4PhotoAssetDto> | V4PageResultDto<V4PhotoAssetDto> | V4PhotoAssetDto[],
): MystudioPhotoAssetPage {
    const collection = normalizeAvailability(raw, "촬영 사진");
    const root = objectValue(raw);
    return {
        ...collection,
        items: collection.items.map((row) => {
            const id = text(row.id);
            const thumbnailUrl = text(row.thumbnailUrl, row.thumbnail_url);
            const viewerUrl = text(row.viewerUrl, row.viewer_url);
            if (!id || !thumbnailUrl || !viewerUrl) invalidResponse("촬영 사진", row);
            return {
                id,
                url: viewerUrl,
                thumbnailUrl,
                downloadUrl: viewerUrl,
                alt: "촬영 사진",
            };
        }),
        page: numberValue(root.page) || 1,
        pageSize: numberValue(root.limit, root.pageSize, root.page_size) || collection.items.length,
        total: numberValue(root.total) || collection.items.length,
        hasNext: Boolean(root.hasNext),
    };
}

export function adaptTheme(row: V4ThemeDto): MystudioTheme {
    const id = text(row.id);
    if (!id) invalidResponse("촬영테마", row);
    return {
        id,
        title: text(row.name, row.title) || "촬영 테마",
        category: text(row.category) || undefined,
        description: text(row.description) || undefined,
        thumbnailUrl: text(row.imageUrl, row.image_url) || undefined,
        images: text(row.imageUrl, row.image_url) ? [{
            id: `${id}-main`,
            url: text(row.imageUrl, row.image_url),
        }] : [],
        tags: [text(row.productName, row.product_name)].filter(Boolean),
        officeIds: text(row.officeId, row.office_id)
            ? [text(row.officeId, row.office_id)]
            : undefined,
        officeName: text(row.officeName, row.office_name) || undefined,
        isFavorite: Boolean(row.favorite),
        selectable: row.selection?.selectable !== false && row.selection?.locked !== true,
        rank: numberValue(row.rank) || undefined,
        rankSource: row.rankSource || row.rank_source,
        popularityScore: numberValue(row.popularityScore, row.popularity_score),
    };
}

export function adaptThemes(
    raw: V4AvailabilityDto<V4ThemeDto> | V4PageResultDto<V4ThemeDto> | V4ThemeDto[],
): MystudioThemeCatalog {
    const collection = normalizeAvailability(raw, "촬영테마");
    const root = Array.isArray(raw) ? {} : raw;
    const offices = Array.isArray(root.offices)
        ? root.offices
            .map((office) => ({
                id: text(office?.id),
                name: text(office?.name),
            }))
            .filter((office) => office.id && office.name)
        : [];
    const defaultOffice = offices[0];
    const allowedOfficeIds = new Set(offices.map((office) => office.id));
    const normalizedItems = collection.items.map(adaptTheme);
    const items = normalizedItems
        .filter((theme) =>
            !defaultOffice
            || !theme.officeIds?.length
            || theme.officeIds.some((officeId) => allowedOfficeIds.has(officeId)))
        .map((theme) => ({
            ...theme,
            officeIds: theme.officeIds || (defaultOffice ? [defaultOffice.id] : undefined),
            officeName: theme.officeName || defaultOffice?.name,
        }));
    const pageRoot = root as V4PageResultDto<V4ThemeDto>;
    const categoryLabels = new Set(items.map((item) => item.category).filter(Boolean) as string[]);
    const reportedTotal = numberValue(pageRoot.total) || items.length;
    return {
        items,
        offices,
        categories: Array.from(categoryLabels).map((category) => ({ id: category, label: category })),
        total: items.length < normalizedItems.length ? items.length : reportedTotal,
        hasNext: Boolean(pageRoot.hasNext),
        availability: collection.availability,
        reasonCode: collection.reasonCode,
    };
}

function adaptContentItem(value: unknown, index: number): MystudioContentItem | null {
    const row = objectValue(value);
    const type = text(row.type, row.contentType, row.content_type);
    if (!["event", "promotion", "notice", "faq"].includes(type)) return null;
    const cta = objectValue(row.cta);
    const id = text(row.id, row.contentId, row.content_id, index);
    return {
        id,
        type: type as MystudioContentItem["type"],
        title: text(row.title),
        summary: text(row.summary) || undefined,
        content: text(row.content) || undefined,
        imageUrl: text(row.imageUrl, row.image_url) || undefined,
        publishedAt: text(row.publishedAt, row.published_at) || undefined,
        expiresAt: text(row.expiresAt, row.expires_at) || undefined,
        officeId: text(row.officeId, row.office_id) || undefined,
        deepLink: text(row.deepLink, row.deep_link) || undefined,
        cta: text(cta.action) ? {
            action: text(cta.action),
            label: text(cta.label) || undefined,
            payload: objectValue(cta.payload),
        } : undefined,
    };
}

export function adaptPublicContent(raw: V4ContentListDto): MystudioCollection<MystudioContentItem> {
    if (!raw || typeof raw !== "object" || !Array.isArray(raw.items)) {
        invalidResponse("앱 콘텐츠", raw);
    }
    const items = raw.items
        .map(adaptContentItem)
        .filter(Boolean) as MystudioContentItem[];
    return {
        items,
        availability: items.length > 0 ? "available" : "empty",
        total: numberValue(raw.count) ?? items.length,
    };
}

export function adaptMyPage(raw: V4MeDto): MystudioMyPage {
    if (!raw || typeof raw !== "object") invalidResponse("회원정보", raw);
    const customerId = text(raw.customerId, raw.customer_id);
    if (!customerId) invalidResponse("회원정보", raw);
    const office = objectValue(raw.office);
    const officeId = text(office.id, raw.officeId, raw.office_id);
    const shipping = objectValue(raw.shipping || raw.shippingAddress || raw.shipping_address);
    const contractsRoot = Array.isArray(raw.contracts)
        ? { items: raw.contracts }
        : objectValue(raw.contracts);
    const contracts = Array.isArray(contractsRoot.items) ? contractsRoot.items : [];
    const paymentsRoot = Array.isArray(raw.payments)
        ? { items: raw.payments }
        : objectValue(raw.payments);
    const payments = Array.isArray(paymentsRoot.items) ? paymentsRoot.items : [];
    const delivery = objectValue(raw.delivery);
    return {
        customerId,
        memberId: text(raw.memberId, raw.member_id, customerId),
        displayName: text(raw.displayName, raw.display_name, raw.customerName, raw.customer_name) || "고객",
        phone: text(raw.phone) || undefined,
        email: text(raw.email) || undefined,
        office: officeId || text(office.name, raw.studioName, raw.studio_name) ? {
            id: officeId,
            name: text(office.name, raw.studioName, raw.studio_name) || "스튜디오 정보 없음",
            phone: text(office.phone, raw.studioPhone, raw.studio_phone) || undefined,
            hours: text(office.hours, raw.studioHours, raw.studio_hours) || undefined,
            address: text(office.address, raw.studioAddress, raw.studio_address) || undefined,
        } : undefined,
        contracts: contracts.map((value, index) => {
            const row = objectValue(value);
            const hasBalance = typeof row.hasBalance === "boolean"
                ? row.hasBalance
                : undefined;
            return {
                id: text(row.id, row.contractId, row.contract_id, index),
                title: text(row.packageName, row.package_name, row.title, row.name) || "계약 상품",
                status: hasBalance === true
                    ? "잔금 있음"
                    : hasBalance === false
                        ? "결제 완료"
                        : text(row.status, row.statusLabel, row.status_label) || "상태 정보 없음",
                contractDate: text(row.contractDate, row.contract_date) || undefined,
                hasBalance,
                totalAmount: numberValue(row.totalAmount, row.total_amount),
                paidAmount: numberValue(row.paidAmount, row.paid_amount),
                remainingAmount: numberValue(row.remainingAmount, row.remaining_amount),
                documents: (
                    Array.isArray(row.documents) ? row.documents : []
                ).map((value) => {
                    const document = objectValue(value);
                    return {
                        id: text(document.id),
                        url: text(document.url),
                        fileName: text(document.fileName, document.file_name) || undefined,
                    };
                }).filter((document) => document.id && document.url),
            };
        }),
        payments: payments.map((value, index) => {
            const row = objectValue(value);
            const source = text(row.source);
            return {
                id: text(row.id, index),
                contractId: text(row.contractId, row.contract_id),
                source: source === "advance" || source === "interim"
                    ? source
                    : "contract",
                type: text(row.type, row.paymentType, row.payment_type) || undefined,
                date: text(row.date, row.paymentDate, row.payment_date) || undefined,
                status: text(row.status) === "PAID" ? "PAID" : "RECORDED",
                amount: numberValue(row.amount) ?? 0,
                approvalNumber: text(
                    row.approvalNumber,
                    row.approval_number,
                ) || undefined,
                proofType: text(row.proofType, row.proof_type) || undefined,
            };
        }),
        shippingAddress: Object.keys(shipping).length > 0 ? {
            id: text(shipping.id) || undefined,
            recipient: text(shipping.recipient, shipping.receiver),
            phone: text(shipping.phone),
            postcode: text(shipping.postalCode, shipping.postal_code, shipping.postcode, shipping.zipCode, shipping.zip_code) || undefined,
            addressLine1: text(shipping.address, shipping.addressLine1, shipping.address_line1),
            addressLine2: text(shipping.addressLine2, shipping.address_line2, shipping.detailAddress, shipping.detail_address) || undefined,
        } : undefined,
        delivery: Object.keys(delivery).length > 0 ? {
            availability: text(delivery.availability) === "available"
                ? "available"
                : "empty",
            id: text(delivery.id) || undefined,
            date: text(delivery.date) || undefined,
            status: text(delivery.status) || undefined,
            trackingAvailable: booleanValue(
                delivery.trackingAvailable,
                delivery.tracking_available,
            ),
            trackingNumber: text(
                delivery.trackingNumber,
                delivery.tracking_number,
            ) || undefined,
            deliveryType: text(
                delivery.deliveryType,
                delivery.delivery_type,
            ) || undefined,
        } : undefined,
    };
}

export function adaptActionItems(raw: unknown): MystudioActionItem[] {
    const root = objectValue(raw);
    const rows = Array.isArray(raw)
        ? raw
        : Array.isArray(root.items) ? root.items : [];
    return rows.map((value) => {
        const row = objectValue(value);
        return {
            code: text(row.code) || "ACTION",
            title: text(row.title) || "확인이 필요한 항목",
            scheduleId: text(row.scheduleId, row.schedule_id) || undefined,
            dueDate: text(row.dueDate, row.due_date) || undefined,
        };
    });
}

export function adaptTerms(raw: unknown): MystudioTermsDocument[] {
    const root = objectValue(raw);
    const rows = Array.isArray(raw)
        ? raw
        : Array.isArray(root.items) ? root.items : [];
    return rows.map((value) => {
        const row = objectValue(value);
        return {
            id: text(row.id),
            type: text(row.type) || "SERVICE",
            version: text(row.version),
            title: text(row.title) || "이용약관",
            content: text(row.content),
            effectiveFrom: text(
                row.effectiveFrom,
                row.effective_from,
            ) || undefined,
            active: booleanValue(row.active),
        };
    }).filter((document) => document.id);
}

export function adaptHome(raw: V4HomeDto): MystudioHome {
    if (!raw || typeof raw !== "object") invalidResponse("홈", raw);
    const schedules = raw.schedules && typeof raw.schedules === "object" ? raw.schedules : {};
    const profile = adaptMyPage({
        ...(raw.profile || {}),
        customerId: raw.profile?.customerId ?? raw.customerId,
        customer_id: raw.profile?.customer_id ?? raw.customer_id,
        officeId: raw.profile?.officeId ?? raw.officeId,
        office_id: raw.profile?.office_id ?? raw.office_id,
        studioName: raw.profile?.studioName ?? raw.studioName,
        studio_name: raw.profile?.studio_name ?? raw.studio_name,
    });
    const productsRaw = raw.products || [];
    const products = adaptProducts(productsRaw as V4AvailabilityDto<V4ProductDto> | V4ProductDto[]);
    const reviewPhotosRaw = raw.reviewPhotos || raw.review_photos || [];
    const galleryPostsRaw = raw.galleryPosts || raw.gallery_posts;
    const gallerySectionsRaw = raw.gallerySections || raw.gallery_sections || [];
    const popularThemesRaw = raw.popularThemes || raw.popular_themes || [];
    const content = raw.content;
    const contentRows = Array.isArray(content)
        ? content
        : content && Array.isArray(content.items) ? content.items : [];
    const contentItems = contentRows.length > 0
        ? contentRows.map(adaptContentItem).filter(Boolean) as MystudioContentItem[]
        : [];
    const communication = raw.communication && typeof raw.communication === "object"
        ? raw.communication
        : undefined;
    const communicationNotices = Array.isArray(communication?.notices)
        ? (communication?.notices || []).map(adaptContentItem).filter(Boolean) as MystudioContentItem[]
        : [];
    const communicationFaqs = Array.isArray(communication?.faqs)
        ? (communication?.faqs || []).map(adaptContentItem).filter(Boolean) as MystudioContentItem[]
        : [];
    const recentInquiriesRaw = communication?.recentInquiries
        || communication?.recent_inquiries
        || [];
    return {
        profile: {
            customerId: profile.customerId,
            memberId: profile.memberId,
            displayName: profile.displayName,
            phone: profile.phone,
        },
        upcomingSchedules: Array.isArray(schedules.upcoming)
            ? schedules.upcoming.map(adaptSchedule)
            : [],
        completedSchedules: (
            Array.isArray(schedules.recent)
                ? schedules.recent
                : Array.isArray(schedules.completed) ? schedules.completed : []
        ).map(adaptSchedule),
        products: products.items,
        reviewEvent: adaptReviewEvent(
            raw.reviewEvent
            || raw.review_event
            || raw.sections?.reviewEvent,
        ),
        reviewPhotos: Array.isArray(reviewPhotosRaw)
            ? reviewPhotosRaw.map(adaptPhotoAsset).filter(Boolean) as MystudioPhotoAsset[]
            : [],
        galleryPosts: galleryPostsRaw
            ? adaptGalleryPosts(galleryPostsRaw).items
            : [],
        gallerySections: Array.isArray(gallerySectionsRaw)
            ? gallerySectionsRaw.map(adaptGallerySection)
            : [],
        events: contentItems.filter((item) => item.type === "event" || item.type === "promotion"),
        notices: communicationNotices.length > 0
            ? communicationNotices
            : contentItems.filter((item) => item.type === "notice"),
        faqs: communicationFaqs.length > 0
            ? communicationFaqs
            : contentItems.filter((item) => item.type === "faq"),
        communication: communication ? {
            recentInquiries: Array.isArray(recentInquiriesRaw)
                ? recentInquiriesRaw.map(adaptInquiry)
                : [],
            unreadReplyCount: numberValue(
                communication.unreadReplyCount,
                communication.unread_reply_count,
            ) ?? 0,
            contact: profile.office,
        } : undefined,
        popularThemes: Array.isArray(popularThemesRaw)
            ? popularThemesRaw.map(adaptTheme)
            : [],
    };
}

function adaptGalleryPhoto(value: unknown): MystudioGalleryPhoto | undefined {
    const row = objectValue(value);
    const id = text(row.id);
    if (!id) return undefined;
    return {
        id,
        imageUrl: text(row.imageUrl, row.image_url) || undefined,
        thumbnailUrl: text(row.thumbnailUrl, row.thumbnail_url) || undefined,
        altText: text(row.altText, row.alt_text) || undefined,
        sortOrder: numberValue(row.sortOrder, row.sort_order) ?? 0,
        uploadedAt: text(row.uploadedAt, row.uploaded_at) || undefined,
    };
}

export function adaptGalleryPost(raw: V4GalleryPostDto): MystudioGalleryPost {
    if (!raw || typeof raw !== "object") invalidResponse("스튜디오 갤러리", raw);
    const id = text(raw.id);
    if (!id) invalidResponse("스튜디오 갤러리", raw);
    const categoryRow = objectValue(raw.category);
    const cover = adaptGalleryPhoto(raw.coverPhoto || raw.cover_photo);
    const photos = Array.isArray(raw.photos)
        ? raw.photos.map(adaptGalleryPhoto).filter(Boolean) as MystudioGalleryPhoto[]
        : undefined;
    return {
        id,
        title: text(raw.title) || "스튜디오 갤러리",
        summary: text(raw.summary) || undefined,
        authorDisplayName: text(
            raw.authorDisplayName,
            raw.author_display_name,
        ) || "MyStudio",
        category: {
            id: text(categoryRow.id, categoryRow.name) || "uncategorized",
            name: text(categoryRow.name, raw.category) || "기타",
            description: text(categoryRow.description) || undefined,
            coverImageUrl: text(
                categoryRow.coverImageUrl,
                categoryRow.cover_image_url,
            ) || undefined,
        },
        officeName: text(raw.officeName, raw.office_name),
        sortOrder: numberValue(raw.sortOrder, raw.sort_order) ?? 0,
        photoCount: numberValue(raw.photoCount, raw.photo_count, raw.total)
            ?? photos?.length
            ?? 0,
        viewCount: numberValue(raw.viewCount, raw.view_count) ?? 0,
        coverPhoto: cover,
        photos,
        createdAt: text(raw.createdAt, raw.created_at) || undefined,
        publishedAt: text(
            raw.publishedAt,
            raw.published_at,
            raw.createdAt,
            raw.created_at,
        ) || undefined,
    };
}

export function adaptGallerySection(
    raw: V4GallerySectionDto,
): MystudioGallerySection {
    if (!raw || typeof raw !== "object") invalidResponse("스튜디오 갤러리 섹션", raw);
    const id = text(raw.id);
    if (!id) invalidResponse("스튜디오 갤러리 섹션", raw);
    const items = Array.isArray(raw.items) ? raw.items.map(adaptGalleryPost) : [];
    return {
        id,
        name: text(raw.name) || "스튜디오 갤러리",
        description: text(raw.description) || undefined,
        coverImageUrl: text(raw.coverImageUrl, raw.cover_image_url) || undefined,
        sortOrder: numberValue(raw.sortOrder, raw.sort_order) ?? 0,
        items,
        total: numberValue(raw.total) ?? items.length,
    };
}

export function adaptGalleryPosts(
    raw: { items?: V4GalleryPostDto[]; page?: number; limit?: number; total?: number; hasNext?: boolean; has_next?: boolean },
): MystudioGalleryPostPage {
    if (!raw || typeof raw !== "object") invalidResponse("스튜디오 갤러리 목록", raw);
    const items = Array.isArray(raw.items) ? raw.items.map(adaptGalleryPost) : [];
    return {
        items,
        page: numberValue(raw.page) ?? 1,
        limit: numberValue(raw.limit) ?? items.length,
        total: numberValue(raw.total) ?? items.length,
        hasNext: booleanValue(raw.hasNext, raw.has_next),
    };
}

export function adaptInquiry(row: V4InquiryDto): MystudioInquiry {
    const id = text(row.id, row.inquiryId, row.inquiry_id);
    if (!id) invalidResponse("문의", row);
    const validStatuses = new Set(["received", "in_progress", "answered", "closed"]);
    const statusKey = text(row.status).trim().toLowerCase();
    const rawStatus = statusKey === "pending" || statusKey === "received"
        ? "received"
        : statusKey === "in_progress" || statusKey === "processing"
            ? "in_progress"
            : statusKey === "answered" || statusKey === "completed"
                ? "answered"
                : statusKey === "closed" || statusKey === "cancelled"
                    ? "closed"
                    : statusKey;
    return {
        id,
        category: text(row.category, row.payload?.category, row.type) || "문의",
        title: text(row.subject, row.title) || "문의",
        content: text(row.message, row.content),
        status: validStatuses.has(rawStatus)
            ? rawStatus as MystudioInquiry["status"]
            : "received",
        createdAt: text(row.createdAt, row.created_at),
        answeredAt: text(row.answeredAt, row.answered_at) || undefined,
        answer: text(row.answer) || undefined,
        unreadReplyCount: numberValue(
            row.unreadReplyCount,
            row.unread_reply_count,
        ) ?? 0,
        messages: Array.isArray(row.messages)
            ? row.messages.map((message, index) => ({
                id: text(message.id, index),
                senderType: (
                    text(message.senderType, message.sender_type).toUpperCase()
                    || "SYSTEM"
                ) as "CUSTOMER" | "STAFF" | "SYSTEM",
                body: text(message.body, message.message),
                createdAt: text(message.createdAt, message.created_at),
                readAt: text(message.readAt, message.read_at) || undefined,
            }))
            : undefined,
        attachments: [],
    };
}

export function adaptInquiries(
    raw: V4PageResultDto<V4InquiryDto> | V4InquiryDto[],
): MystudioCursorPage<MystudioInquiry> {
    const collection = normalizeAvailability(raw, "문의");
    const root = Array.isArray(raw) ? {} : raw;
    return {
        items: collection.items.map(adaptInquiry),
        total: collection.total,
        nextCursor: text(root.nextCursor, root.next_cursor) || null,
        availability: collection.availability,
        reasonCode: collection.reasonCode,
    };
}

export function adaptNotification(row: V4NotificationDto): MystudioNotification {
    const id = text(row.id, row.notificationId, row.notification_id);
    if (!id) invalidResponse("알림", row);
    return {
        id,
        category: text(row.category) || "알림",
        title: text(row.title) || "알림",
        body: text(row.body, row.content),
        createdAt: text(row.createdAt, row.created_at),
        readAt: text(row.readAt, row.read_at) || undefined,
        deepLink: text(row.deepLink, row.deep_link) || undefined,
        relatedResourceId: text(row.relatedResourceId, row.related_resource_id) || undefined,
    };
}

export function adaptNotifications(
    raw: V4PageResultDto<V4NotificationDto> | V4NotificationDto[],
): MystudioCursorPage<MystudioNotification> {
    const collection = normalizeAvailability(raw, "알림");
    const root = Array.isArray(raw) ? {} : raw;
    return {
        items: collection.items.map(adaptNotification),
        total: collection.total,
        unreadCount: Number.isFinite(Number(root.unreadCount))
            ? Number(root.unreadCount)
            : 0,
        nextCursor: text(root.nextCursor, root.next_cursor) || null,
        availability: collection.availability,
        reasonCode: collection.reasonCode,
    };
}
